/**
 * Step-level validation logic
 */

import {
    validateName,
    validateEmail,
    validateBirthday,
    validateSex,
    validateCity,
    validateHomeCity,
    validatePassword,
    validateUsername
} from './validators.js';

const FIELD_MAP = ['name', 'birthday', 'sex', 'currentCity', 'homeCity', 'password', 'username', 'email'];

export function getFieldForStep(step) {
    return FIELD_MAP[step - 1];
}

export function isStepValid(step, state) {
    switch (step) {
        case 1:
            return validateName(state.name).valid;
        case 2:
            return validateBirthday(state.day, state.month, state.year).valid;
        case 3:
            return validateSex(state.sex).valid;
        case 4:
            return validateCity(state.cityId).valid;
        case 5:
            return validateHomeCity(state.homeCityId).valid;
        case 6:
            return validatePassword(state.password).valid;
        case 7:
            return validateUsername(state.username).valid;
        case 8:
            return validateEmail(state.email).valid;
        default:
            return true;
    }
}

export function getStepError(step, state) {
    const field = getFieldForStep(step);

    // Don't show errors during initialization
    if (state.isInitializing) {
        return null;
    }

    // Don't show errors until field is touched
    if (!state.touched[field]) {
        return null;
    }

    // Check server errors FIRST (they are authoritative)
    if (state.errors[field]?.message) {
        return state.errors[field].message;
    }
    if (state.errors[field]?.msg) {
        return state.errors[field].msg;
    }
    if (typeof state.errors[field] === 'string') {
        return state.errors[field];
    }

    // If the step is valid, don't show errors (handles case where touched
    // is set during init but data is already valid from localStorage)
    if (isStepValid(step, state)) return null;

    let validationResult;
    switch (step) {
        case 1:
            validationResult = validateName(state.name);
            break;
        case 2:
            validationResult = validateBirthday(state.day, state.month, state.year);
            break;
        case 3:
            validationResult = validateSex(state.sex);
            break;
        case 4:
            validationResult = validateCity(state.cityId);
            break;
        case 5:
            validationResult = validateHomeCity(state.homeCityId);
            break;
        case 6:
            validationResult = validatePassword(state.password);
            break;
        case 7:
            validationResult = validateUsername(state.username);
            break;
        case 8:
            validationResult = validateEmail(state.email);
            break;
        default:
            return null;
    }

    // Show client-side validation error if field is invalid
    if (!validationResult.valid) {
        return validationResult.error;
    }

    return null;
}

export function getFirstInvalidStep(state, totalSteps) {
    for (let step = 1; step <= totalSteps; step++) {
        if (!isStepValid(step, state)) {
            return step;
        }
    }
    return null;
}

export function markStepTouched(step, state) {
    const field = getFieldForStep(step);
    state.touched[field] = true;
}
